import {
  ReadonlyURLSearchParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { useCallback } from 'react';

import { isDefined } from '@/utils/guards';

export const useEnhancedSearchParams = (): {
  /** Functions the same as the default export from `useSearchParams` from `next/navigation` */
  searchParams: ReadonlyURLSearchParams | null;
  /** Return a query string param if present */
  getParam: (key: string) => string | null;
  /** Will set or replace a search parameter with a new value and update the URL quickly without a full page rerender */
  setOrReplaceParam: (key: string, value: string) => void;
  /** Will set or replace multiple search parameters with new values and update the URL quickly without a full page rerender */
  setOrReplaceParams: (
    params: Record<string, string | null | undefined>
  ) => void;
  /**
   * This does a `router.replace()` which will rerender the page and allow the server to handle the search params since
   * the `searchParams` passed into a next page will update doing it this way
   */
  setOrReplaceParamWithRerender: (key: string, value: string) => void;
  /** Will delete a search parameter from the URL and update it quickly */
  deleteParam: (key: string) => void;
  /** Will delete multiple search parameters from the URL and update it quickly */
  deleteParams: (keys: string[]) => void;
} => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getParam = useCallback(
    (key: string) => searchParams?.get(key) || null,
    [searchParams]
  );

  const setOrReplaceParam = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams?.toString());

      newParams.set(key, value);

      // If there is no value then get rid of it
      if (!value) {
        newParams.delete(key);
      }

      // This does a "quick replace" of the search params in the URL while also rerendering `searchParams`
      window.history.replaceState(null, '', `?${newParams?.toString()}`);
    },
    [searchParams]
  );

  const setOrReplaceParams = useCallback(
    (params: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams?.toString());
      Object.entries(params).forEach(([key, value]) => {
        if (isDefined(value)) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });

      window.history.replaceState(null, '', `?${newParams?.toString()}`);
    },
    [searchParams]
  );

  const setOrReplaceParamWithRerender = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams?.toString());

      newParams.set(key, value);

      // If there is no value then get rid of it
      if (!value) {
        newParams.delete(key);
      }

      router.replace(`${pathname}?${newParams?.toString()}`);
    },
    [pathname, router, searchParams]
  );

  const deleteParam = useCallback(
    (key: string) => {
      const newParams = new URLSearchParams(searchParams?.toString());
      newParams.delete(key);
      window.history.replaceState(null, '', `?${newParams?.toString()}`);
    },
    [searchParams]
  );

  const deleteParams = useCallback(
    (keys: string[]) => {
      const newParams = new URLSearchParams(searchParams?.toString());
      keys.forEach((key) => newParams.delete(key));
      window.history.replaceState(null, '', `?${newParams?.toString()}`);
    },
    [searchParams]
  );

  return {
    deleteParam,
    deleteParams,
    getParam,
    searchParams,
    setOrReplaceParam,
    setOrReplaceParamWithRerender,
    setOrReplaceParams,
  };
};
